import { React, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import urls, { getCsrfToken } from './urls.js';
import { clicks } from '../com.zedock.common.script/common.js';

import '../com.zedock.common.style/ForgotPassword.css';
import '../com.zedock.common.style/common.css';

const STEP_EMAIL = 1;
const STEP_OTP = 2;
const STEP_RESET = 3;

function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    clicks(location.pathname);
  }, []);

  // ---------------- Step 1: send OTP to email ----------------
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${urls.BASE_URL}/forgot/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken()
        },
        body: JSON.stringify({ email })
      });
      const data = await response.json().catch(() => ({}));


      if (response.ok) {
        if (data.responseData === 'false') {
          throw new Error(data.message || 'Unable to send OTP. Please try again.');
        }
        setMessage('An OTP has been sent to your email.');
      }
      setStep(STEP_OTP);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Step 2: verify OTP ----------------
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!otp || otp.length < 4) {
      setError('Please enter the OTP sent to your email.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${urls.BASE_URL}/forgot/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken()
        },
        body: JSON.stringify({ email, otp })
      });
      const data = await response.json().catch(() => ({}));


      if (response.ok) {
        if (data.responseData === 'false') {
          throw new Error(data.message || 'Invalid or expired OTP.');
        }
        setMessage('OTP verified. Please set a new password.');
      }
      setStep(STEP_RESET);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Step 3: reset password ----------------
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newPwd || !confirmPwd) {
      setError('Please fill in both password fields.');
      return;
    }
    if (newPwd.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${urls.BASE_URL}/forgot/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken()
        },
        body: JSON.stringify({ email, newPassword: newPwd, otp })
      });
      const data = await response.json().catch(() => ({}));


      if (response.ok) {
        if (data.responseData === 'false') {
          throw new Error(data.message || 'Unable to reset password. Please try again.');
        }
        setMessage('Password reset successfully! Redirecting to login...');
      }

      setTimeout(() => navigate("/Login", { state: { from: location } }), 1800);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${urls.BASE_URL}/forgot/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCsrfToken()
        },
        body: JSON.stringify({ email })
      });
      const data = await response.json().catch(() => ({}));


      if (response.ok) {
        if (data.responseData === 'false') {
          throw new Error('Unable to resend OTP.');
        }
        setMessage('A new OTP has been sent to your email.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container px-0    ">
      <div className="zd-wrap px-2 px-lg-4 py-3">

        <div className="row m-1">
          <nav aria-label="breadcrumb" className="zd-crumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="" onClick={(e) => { e.preventDefault();navigate("/Product")}}>Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Forgot Password</li>
            </ol>
          </nav>
        </div>


        <div className="row justify-content-center  m-1">
          <div className="col-12 col-lg-10">
            <div className="zd-auth-shell row g-0">

              {/* image side */}
              <div className="col-12 col-lg-6  d-none d-lg-block px-0">
                <div className="zd-auth-shell">

                  <div className="zd-auth-visual h-100 ">
                    <img
                      src={urls.CDN_URL + 'common/logsign.webp'} 
                      alt="Zedock fashion" />
                    <div className="zd-auth-visual-caption">
                      <span className="eyebrow">Welcome back</span>
                      <h3>“Pick up right where you left off — your saved items are waiting.”</h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-6   ">
                <div className="zd-auth-shell">
                  <div className="zd-auth-form">

                    <span className="zd-auth-eyebrow">Zedock account</span>
                    <h2>Forgot password</h2>
                    <p className="zd-auth-sub">
                      {step === STEP_EMAIL && "Enter your registered email and we'll send you an OTP."}
                      {step === STEP_OTP && `Enter the OTP sent to ${email}`}
                      {step === STEP_RESET && 'Create a new password for your account.'}
                    </p>

                    {/* step indicator */}
                    <div className="zd-step-indicator">
                      <div className={`zd-step ${step >= STEP_EMAIL ? 'zd-step-active' : ''}`}>1</div>
                      <div className="zd-step-line"></div>
                      <div className={`zd-step ${step >= STEP_OTP ? 'zd-step-active' : ''}`}>2</div>
                      <div className="zd-step-line"></div>
                      <div className={`zd-step ${step >= STEP_RESET ? 'zd-step-active' : ''}`}>3</div>
                    </div>

                    {error && <div className="zd-alert zd-alert-error">{error}</div>}
                    {message && <div className="zd-alert zd-alert-success">{message}</div>}

                    {/* ---------------- STEP 1: EMAIL ---------------- */}
                    {step === STEP_EMAIL && (
                      <form onSubmit={handleSendOtp}>
                        <div className="zd-field">
                          <label htmlFor="email">Email address</label>
                          <input
                            type="email"
                            id="email"
                            className="form-control"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus
                          />
                        </div>

                        <button type="submit" className="zd-submit" disabled={loading}>
                          {loading ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                      </form>
                    )}

                    {/* ---------------- STEP 2: OTP ---------------- */}
                    {step === STEP_OTP && (
                      <form onSubmit={handleVerifyOtp}>
                        <div className="zd-field">
                          <label htmlFor="otp">Enter OTP</label>
                          <input
                            type="text"
                            id="otp"
                            inputMode="numeric"
                            maxLength={6}
                            className="form-control zd-otp-input"
                            placeholder="- - - - - -"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                            autoFocus
                          />
                        </div>

                        <div className="zd-row-between">
                          <button
                            type="button"
                            className="zd-forgot"
                            onClick={resendOtp}
                            disabled={loading}
                          >
                            Resend OTP
                          </button>
                          <button
                            type="button"
                            className="zd-forgot"
                            onClick={() => { setStep(STEP_EMAIL); setOtp(''); setError(''); setMessage(''); }}
                          >
                            Change email
                          </button>
                        </div>

                        <button type="submit" className="zd-submit" disabled={loading}>
                          {loading ? 'Verifying...' : 'Verify OTP'}
                        </button>
                      </form>
                    )}

                    {/* ---------------- STEP 3: RESET PASSWORD ---------------- */}
                    {step === STEP_RESET && (
                      <form onSubmit={handleResetPassword}>
                        <div className="zd-field">
                          <label htmlFor="newPwd">New password</label>
                          <input
                            type="password"
                            id="newPwd"
                            className="form-control"
                            placeholder="At least 8 characters"
                            value={newPwd}
                            onChange={(e) => setNewPwd(e.target.value)}
                            autoFocus
                          />
                        </div>

                        <div className="zd-field">
                          <label htmlFor="confirmPwd">Re-enter password</label>
                          <input
                            type="password"
                            id="confirmPwd"
                            className="form-control"
                            placeholder="Re-enter new password"
                            value={confirmPwd}
                            onChange={(e) => setConfirmPwd(e.target.value)}
                          />
                        </div>

                        <button type="submit" className="zd-submit" disabled={loading}>
                          {loading ? 'Resetting...' : 'Reset password'}
                        </button>
                      </form>
                    )}

                    <p className="zd-alt-action">
                      Remembered your password? <a onClick={() => navigate("/Login", { state: { from: location } })}>Back to login</a>
                    </p>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
